import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewTableComponent from '../../../components/NewTableComponent';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { partModelStatusOptions } from '../../../utils/Dropdowns';
import { Badge } from 'react-bootstrap';
import PartMakeOverviewCards from './PartMakeOverviewCards';
import CreatePartMakeModal from './CreatePartMakeModal';
import UpdatePartMakeModal from './UpdatePartMakeModal';
import { plusIcon } from '../../../utils/svgs';
import Button from '../../../components/button';
import { LuPencil } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import CustomModal from '../../../components/CustomModal';
import { deletePartMakeAsync, fetchPartMakes, getPartMakesOverview } from '../../../store/Slices/PartMakeSlice';
import { errorNotify, successNotify } from '../../../utils/toast';

const PartMakes = () => {
    const dispatch = useDispatch();
    const { partMakesData, partMakesLoading, deletePartMakeLoading } = useSelector((state) => state.partMakes);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [searchFilters, setSearchFilters] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPartMake, setSelectedPartMake] = useState(null);
    const [selectedPartMakeId, setSelectedPartMakeId] = useState(null);

    const handleEditPartMake = (partMake) => {
        setSelectedPartMake(partMake);
        setShowUpdateModal(true);
    };

    const handleDeletePartMake = async () => {
        try {
            await dispatch(deletePartMakeAsync(selectedPartMakeId)).unwrap();
            successNotify('Part make deleted successfully');
            setShowDeleteModal(false);
            setSelectedPartMakeId(null);
            
            // Refresh table and overview
            dispatch(fetchPartMakes({
                page: currentPage,
                limit: limit,
                body: {}
            }));
            dispatch(getPartMakesOverview());
        } catch (error) {
            errorNotify(error);
        }
    };

    useEffect(() => {
        const transformedFilters = {};
        Object.keys(searchFilters).forEach(key => {
            let value = searchFilters[key].value;
            let filterType = searchFilters[key].filter_type || 'contains';

            if (Array.isArray(value)) {
                filterType = 'one_of';
            } else if (filterType === 'one_of') {
                value = [value];
            }

            if (key === 'statusFilter' && !Array.isArray(value)) {
                filterType = 'equals';
            }

            transformedFilters[key] = {
                value: value,
                filter_type: filterType
            };
        });

        dispatch(fetchPartMakes({
            page: currentPage,
            limit: limit,
            body: {
                ...transformedFilters,
                search_text: searchText
            }
        }));
    }, [currentPage, limit, searchText, searchFilters, dispatch]);

    const columns = [
        {
            name: "ID",
            key: "id",
            selector: (row) => row?.id,
            width: "80px",
            tableColumn: true,
            filterName: "idFilter",
            type: "number",
        },
        {
            name: "Name",
            key: "name",
            selector: (row) => row?.name,
            tableColumn: true,
            filterName: "nameFilter",
            type: "text",
        },
        {
            name: "Status",
            key: "status",
            selector: (row) => (
                <Badge bg={row?.is_active == true ? 'success' : 'danger'} className="text-capitalize">
                    {row?.is_active == true ? 'Active' : 'Inactive'}
                </Badge>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            options: partModelStatusOptions
        },
        {
            name: "Created At",
            key: "created_at",
            selector: (row) => row?.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
            width: "150px",
        },
        {
            name: "Actions",
            key: "actions",
            width: "100px",
            selector: (row) => (
                <div className="table_action_btn">
                    <button onClick={() => handleEditPartMake(row)} className="edit_btn">
                        <LuPencil />
                    </button>
                    <button onClick={() => {
                        setSelectedPartMakeId(row?.id);
                        setShowDeleteModal(true);
                    }} className="delete_btn">
                        <RiDeleteBinLine />
                    </button>
                </div>
            ),
        }
    ];

    return (
        <div className="container-fluid py-4">
            <Breadcrumbs values={['Maintenance', 'Part Makes']} />

            <div className='page_header access_view mb-4'>
                <h2>Part Makes</h2>
                <div className='d-flex align-items-center gap-2'>
                    <Button
                        submitText={<span>{plusIcon} Add Part Make</span>}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={true}
                        submitBtn={() => setShowCreateModal(true)}
                        isShowCancel={false}
                    />
                </div>
            </div>
            
            <PartMakeOverviewCards />

            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <NewTableComponent
                        pageName="Part Makes"
                        columns={columns}
                        data={partMakesData?.results || []}
                        totalItems={partMakesData?.meta?.totalItems || 0}
                        totalPages={partMakesData?.meta?.totalPages || 0}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        loading={partMakesLoading}
                        onSearch={(text) => {
                            setSearchText(text);
                            setCurrentPage(1);
                        }}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                    />
                </div>
            </div>

            <CreatePartMakeModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                currentPage={currentPage}
                limit={limit}
            />

            <UpdatePartMakeModal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedPartMake(null);
                }}
                partMake={selectedPartMake}
                currentPage={currentPage}
                limit={limit}
            />

            <CustomModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} size="md" heading="Delete Part Make" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this part make?</p>

                    <Button
                        cancelBtn={() => setShowDeleteModal(false)}
                        submitBtn={handleDeletePartMake}
                        loading={deletePartMakeLoading}
                        disabled={deletePartMakeLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                        isShowCancel={true}
                    />
                </div>
            </CustomModal>
        </div>
    );
};

export default PartMakes;
